import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, BookOpen, Gem, Compass } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  article: { label: 'Article', icon: BookOpen },
  designer: { label: 'Designer', icon: Gem },
  guide: { label: 'Guide', icon: Compass },
};

const groupOrder = [
  ['articles', 'Articles'],
  ['designers', 'Designers'],
  ['guides', 'Guides'],
];

/**
 * Header search bar with debounced live results.
 *
 * Props:
 *   variant: 'desktop' | 'mobile'
 *   onNavigate: optional callback fired after successful navigation
 *               (used by the mobile sheet to close itself).
 */
export const SearchBar = ({ variant = 'desktop', onNavigate }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(variant === 'mobile');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Outside-click close (desktop only)
  useEffect(() => {
    if (variant !== 'desktop') return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [variant]);

  // Auto-focus when opened on desktop
  useEffect(() => {
    if (open && variant === 'desktop' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open, variant]);

  // Debounced fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/search`, { params: { q: query, limit: 6 } });
        setResults(res.data);
      } catch (err) {
        setResults({ total: 0, results: { articles: [], designers: [], guides: [] } });
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleSelect = (url) => {
    setOpen(variant === 'mobile');
    setQuery('');
    setResults(null);
    navigate(url);
    if (onNavigate) onNavigate();
  };

  const renderResults = () => {
    if (query.trim().length < 2) {
      return (
        <div className="px-5 py-6 text-xs tracking-[0.15em] uppercase text-[#888]">
          Type at least 2 characters
        </div>
      );
    }
    if (loading) {
      return (
        <div className="px-5 py-6 flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#888]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
        </div>
      );
    }
    if (!results || results.total === 0) {
      return (
        <div className="px-5 py-6 text-xs tracking-[0.15em] uppercase text-[#888]">
          No results for “{query}”
        </div>
      );
    }
    return (
      <div className="py-2 max-h-[60vh] overflow-y-auto">
        {groupOrder.map(([key, label]) => {
          const group = results.results[key] || [];
          if (group.length === 0) return null;
          return (
            <div key={key} className="px-2 pb-2">
              <p className="px-3 pt-3 pb-2 text-[10px] tracking-[0.25em] uppercase text-[#C69C6D] font-sans">
                {label}
              </p>
              {group.map((item, i) => {
                const Meta = TYPE_META[item.type] || TYPE_META.article;
                const Icon = Meta.icon;
                return (
                  <button
                    key={`${key}-${i}`}
                    onClick={() => handleSelect(item.url)}
                    className="w-full text-left flex items-start gap-3 px-3 py-2.5 hover:bg-[#FAF9F6] transition-colors group"
                    data-testid={`search-result-${item.type}`}
                  >
                    <span className="mt-0.5 w-7 h-7 flex items-center justify-center border border-[#E5E0D8] text-[#C69C6D] group-hover:border-[#C69C6D] transition-colors">
                      <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-heading text-[15px] text-[#111] leading-snug truncate">
                        {item.title}
                      </span>
                      {item.excerpt && (
                        <span className="block text-xs text-[#666] mt-0.5 line-clamp-1">
                          {item.excerpt}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  // ---------- Mobile (always-open inline) ----------
  if (variant === 'mobile') {
    return (
      <div className="px-6 pt-2 pb-4 border-b border-[#E5E0D8]" data-testid="search-mobile">
        <div className="flex items-center gap-2 border border-[#E5E0D8] bg-white px-3 py-2.5">
          <Search className="w-4 h-4 text-[#888]" strokeWidth={1.5} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles, designers, guides…"
            className="flex-1 bg-transparent outline-none text-sm text-[#111] placeholder:text-[#999]"
            data-testid="search-input-mobile"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="text-[#888] hover:text-[#111]"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
        {(query || loading) && (
          <div className="mt-2 border border-[#E5E0D8] bg-white">
            {renderResults()}
          </div>
        )}
      </div>
    );
  }

  // ---------- Desktop (icon → expanding popover) ----------
  return (
    <div ref={containerRef} className="relative" data-testid="search-desktop">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open search"
          className="p-2 text-[#555] hover:text-[#111] transition-colors"
          data-testid="search-toggle-button"
        >
          <Search className="w-4 h-4" strokeWidth={1.5} />
        </button>
      ) : (
        <div className="flex items-center border-b border-[#111] gap-2 pb-1.5 w-64 transition-all">
          <Search className="w-4 h-4 text-[#111]" strokeWidth={1.5} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the magazine…"
            className="flex-1 bg-transparent outline-none text-sm text-[#111] placeholder:text-[#999] tracking-wide"
            data-testid="search-input"
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
            }}
          />
          <button
            onClick={() => {
              setQuery('');
              setOpen(false);
            }}
            aria-label="Close search"
            className="text-[#888] hover:text-[#111]"
            data-testid="search-close-button"
          >
            <X className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        </div>
      )}

      {open && (query || loading) && (
        <div
          className="absolute right-0 top-[calc(100%+12px)] w-[420px] max-w-[90vw] bg-white border border-[#E5E0D8] shadow-[0_24px_60px_-20px_rgba(17,17,17,0.18)] z-50"
          data-testid="search-results-panel"
        >
          {renderResults()}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
